/**
 * Shared parts for the home "recent work" widgets (conversations / documents /
 * scopes). Loading, empty, and error states plus the row timestamp and the
 * staggered entrance are identical across all three; each widget keeps only its
 * own data fetch and row body.
 */
import { formatDistanceToNow } from "date-fns";
import { AlertCircle, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@/utils";

// Relative "x days ago" with the absolute date on hover, right-aligned in a row.
export function RelativeTime({ iso }: { iso: string }) {
    return (
        <time
            dateTime={iso}
            title={formatDate(iso, "PPp")}
            className="text-muted-foreground shrink-0 self-center text-xs tabular-nums"
        >
            {formatDistanceToNow(new Date(iso), { addSuffix: true })}
        </time>
    );
}

export function RecentWidgetSkeleton({
    noun,
    count = 3,
    twoLine = true,
    className = "flex flex-col gap-1",
}: {
    noun: string;
    count?: number;
    twoLine?: boolean;
    className?: string;
}) {
    return (
        <div className={className}>
            {Array.from({ length: count }, (_, i) => (
                <div
                    key={i}
                    role="status"
                    aria-label={`Loading recent ${noun}`}
                    className="flex items-center gap-2.5 px-3 py-2.5"
                >
                    <Skeleton className="size-8 shrink-0 rounded-lg" />
                    {twoLine ? (
                        <div className="flex flex-1 flex-col gap-1.5">
                            <Skeleton className="h-3.5 w-1/2" />
                            <Skeleton className="h-3 w-1/3" />
                        </div>
                    ) : (
                        <Skeleton className="h-3.5 flex-1" />
                    )}
                    <Skeleton className="h-3 w-12 shrink-0" />
                </div>
            ))}
        </div>
    );
}

export function RecentWidgetEmpty({
    icon,
    title,
    description,
}: {
    icon: ReactNode;
    title: string;
    description: string;
}) {
    return (
        <Empty className="py-8">
            <EmptyHeader>
                <EmptyMedia variant="icon">{icon}</EmptyMedia>
                <EmptyTitle>{title}</EmptyTitle>
                <EmptyDescription>{description}</EmptyDescription>
            </EmptyHeader>
        </Empty>
    );
}

export function RecentWidgetError({
    noun,
    onRetry,
}: {
    noun: string;
    onRetry: () => void;
}) {
    return (
        <Empty className="py-8">
            <EmptyHeader>
                <EmptyMedia
                    variant="icon"
                    className="bg-destructive/10 text-destructive"
                >
                    <AlertCircle />
                </EmptyMedia>
                <EmptyTitle>Couldn't load recent {noun}</EmptyTitle>
                <EmptyDescription>
                    Something went wrong reaching your {noun}.
                </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
                <Button variant="outline" size="sm" onClick={onRetry}>
                    <RefreshCw className="size-3.5" />
                    Retry
                </Button>
            </EmptyContent>
        </Empty>
    );
}
